import cx from "classnames";
import { CTA } from "common/styleguide/CTA";
import { ModalOverlay } from "common/styleguide/ModalOverlay";
import blueTheme from "common/styleguide/ModalOverlay/blueHeaderModalTheme.scss";
import { FiltersCrumbs } from "features/search/FiltersCrumbs";
import { SearchBar } from "features/search/SearchBar";
import { SearchResults } from "features/search/SearchResults";
import { List } from "immutable";
import { loadClubs } from "lib/api/currentUser";
import * as searchActions from "lib/api/search";
import { commonVerbs, Msg } from "lib/localization";
import { loadStatus } from "lib/promiseMiddlewareHelpers";
import React from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { connect, Provider } from "react-redux";
import { initStore } from "../redux/initStore";
import styles from "./LoadExisting.scss";

export const LOAD_EXISTING_MODAL = "LOAD_EXISTING_MODAL";

export const loadExistingMessages = defineMessages({
  addToPlanner: {
    id: "LoadExisting.submitLabel",
    defaultMessage: "Add to Planner",
  },
  importRoutes: {
    id: "CollectionListing.importRoutes",
    defaultMessage: "Import Routes",
  },
  importRoutesAndRides: {
    id: "CollectionListing.importRoutesAndRides",
    defaultMessage: "Import Routes & Rides",
  },
});

const store = initStore();

export const resultTypes = {
  CLUB: 2,
  USER: 1,
  PUBLIC: 3,
};

class Footer extends React.Component {
  render() {
    const { numSelected, files, onAdd, onCancel, submitLabel, collapse } = this.props;

    const totalSelected = numSelected + files.size;
    return (
      <div className={styles.Footer}>
        <CTA inline link margins={false} onClick={onCancel}>
          <Msg capitalized m={commonVerbs.cancel} />
        </CTA>
        <div className={cx(styles.selected, collapse && styles.collapse)}>
          {totalSelected > 0 && (
            <T
              id="LoadExisting.selected"
              defaultMessage="{numSelected} Selected"
              values={{ numSelected: totalSelected }}
              description="An indicator for the number of selected items from a list"
            />
          )}
        </div>
        <CTA inline margins={false} onClick={onAdd}>
          {submitLabel || <Msg m={loadExistingMessages.addToPlanner} />}
        </CTA>
      </div>
    );
  }
}

class LoadExisting extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selected: List(),
      files: List(),
      uploadPending: false,
    };
  }

  componentDidMount = () => {
    const { loadClubs, currentUser } = this.props;

    const clubsLoaded = currentUser && currentUser.clubs && !!currentUser.clubs.get("loadStatus");
    loadClubs && !clubsLoaded && loadClubs();

    this.doSearch(this.defaultQuery());
  };

  defaultQuery() {
    if (this.props.routesOnly) {
      return searchActions.defaultQuery().set("trips", false);
    } else {
      return searchActions.defaultQuery();
    }
  }

  doSearch = (query) => {
    const { search, currentUser, allowedResultTypes } = this.props;

    const defaultQuery = this.defaultQuery();
    const isDefaultQuery = query.toMap().every((value, key) => defaultQuery.get(key) === value);

    if (isDefaultQuery) {
      const userSearch = defaultQuery.merge({
        user_id: currentUser.id,
        sort_by: "created+desc",
      });
      search(userSearch, defaultQuery, { type: resultTypes.USER });
      return;
    }

    if (!allowedResultTypes || allowedResultTypes.includes(resultTypes.USER)) {
      search(query.merge({ user_id: currentUser.id }), query, {
        type: resultTypes.USER,
      });
    }
    if (!allowedResultTypes || allowedResultTypes.includes(resultTypes.PUBLIC)) {
      search(query, query, { type: resultTypes.PUBLIC });
    }
    if (!allowedResultTypes || allowedResultTypes.includes(resultTypes.CLUB)) {
      if (currentUser.clubs.get("loadStatus") === loadStatus.fulfilled) {
        currentUser.clubs
          .get("records")
          .entrySeq()
          .forEach(([id, c]) => {
            search(query.merge({ user_id: c.get("user_id") }), query, {
              type: resultTypes.CLUB,
            });
          });
      }
    }
  };

  loadMore = () => {
    const { query, search } = this.props;

    return (newQuery, meta) => {
      search(newQuery, query, meta);
    };
  };

  pickResult = (result) => {
    const { preventMultiselect } = this.props;
    const { selected } = this.state;

    if (selected.find((r) => r.get("type") === result.get("type") && r.get("id") === result.get("id"))) {
      this.setState({
        selected: selected.filterNot((r) => r.get("type") === result.get("type") && r.get("id") === result.get("id")),
      });
    } else {
      this.setState({
        selected: selected.size > 0 && preventMultiselect ? List([result]) : selected.push(result),
      });
    }
  };

  onAdd = () => {
    const { loadRoute, loadMultiple, loadFile, onRequestClose } = this.props;
    const { selected, files } = this.state;

    loadRoute &&
      selected.forEach((r) => {
        loadRoute(r.get("id"), r.get("type"));
      });

    loadMultiple && loadMultiple(selected.map((r) => [r.get("id"), r.get("type")]));

    loadFile && files.forEach((file) => loadFile(file));

    // TODO: should I wait until all are sucessful?
    onRequestClose();
  };

  onCancel = () => {
    this.props.onRequestClose();
  };

  onFileChange = (files) => {
    if (!files[0]) {
      return;
    }

    const parseFiles = async (files) => {
      for (var i = 0; i < files.length; i++) {
        // smh no files.forEach
        const body = new window.FormData();
        body.append("file", files[i]);

        // this is window.fetch instead of fetchJSON bc the latter stringifies body
        await window
          .fetch("/routes/parse_file.json", {
            method: "POST",
            body,
          })
          .then((response) => response.json())
          .then((result) => {
            this.setState({ files: this.state.files.concat(result) });
          });
      }
    };

    this.setState({ uploadPending: true }, () => {
      parseFiles(files).then(() => this.setState({ uploadPending: false }));
    });
  };

  removeFile = (idx) => {
    this.setState({ files: this.state.files.delete(idx) });
    this.fileInput.value = null; // so we can retrigger onFileChange
  };

  render() {
    const { query, currentUser, results, onRequestClose, routesOnly, submitLabel, collapse, loadFile, intl } =
      this.props;
    const { selected, files, uploadPending } = this.state;

    const metricUnits = currentUser.preferences.get("metric_units", false);

    // Add up the counts for all the groups we're showing
    const resultsCount = results.entrySeq().reduce((sum, [q, rg]) => sum + rg.get("results_count"), 0);

    return (
      <ModalOverlay
        title={intl.formatMessage(
          routesOnly ? loadExistingMessages.importRoutes : loadExistingMessages.importRoutesAndRides
        )}
        subtitle={
          <SearchBar
            doSearch={(q) => this.doSearch(q)}
            query={query}
            metricUnits={metricUnits}
            currentUser={currentUser}
            loadFile={loadFile} // show or hide upload file button based on presence of this function
            routesOnly={routesOnly}
            uploadPending={uploadPending}
            fileInputRef={this.fileInput}
            intl={intl}
          />
        }
        footer={
          <Footer
            collapse={collapse}
            onCancel={this.onCancel}
            onAdd={this.onAdd}
            numSelected={selected.size}
            files={files}
            submitLabel={submitLabel}
          />
        }
        maxWidth="800px"
        contentPadding={false}
        onRequestClose={onRequestClose}
        theme={blueTheme}
      >
        <input
          style={{ display: "none" }}
          type="file"
          multiple
          ref={(input) => {
            this.fileInput = input;
          }}
          onChange={(e) => this.onFileChange(e.target.files)}
        />
        <div className={styles.LoadExisting}>
          <div className={styles.meta}>
            <div className={styles.results}>
              <T
                id="LoadExisting.resultsFound"
                defaultMessage="{resultsCount} results found."
                values={{
                  resultsCount: (resultsCount + files.size).toLocaleString(),
                }}
              />
            </div>
            <FiltersCrumbs query={query} doSearch={(q) => this.doSearch(q)} metricUnits={metricUnits} />
          </div>
          <SearchResults
            intl={intl}
            routesOnly={routesOnly}
            results={results}
            currentUser={currentUser}
            selectedResults={selected}
            files={files}
            removeFile={this.removeFile}
            clickResult={this.pickResult}
            loadMore={this.loadMore()}
          />
        </div>
      </ModalOverlay>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { search, currentUser } = state;
  return {
    query: search.get("key"),
    queries: search.get("queries"),
    results: search.get("results"),
    currentUser: currentUser,
  };
};

const mapDispatchToProps = {
  search: searchActions.search,
  loadClubs: loadClubs,
};

export const LoadExistingContainer = connect(mapStateToProps, mapDispatchToProps)(injectIntl(LoadExisting));

export const LoadExistingApp = (props) => (
  <Provider store={store}>
    <LoadExistingContainer {...props} />
  </Provider>
);
